import React from 'react';
import { Text, View } from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { BlogPost } from '@/src/domain/models/BlogPost';

const SwissContent: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      The Swiss system tournament is a format designed to handle large fields of players
      efficiently while still producing fair, meaningful matchups. Players are never
      eliminated — instead, each round pairs players with similar records together.
      After a set number of rounds, standings determine the final ranking. It's the
      standard format for chess tournaments worldwide and increasingly popular across
      trading card games, esports, and board game competitions.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      The origin of the Swiss system
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      The Swiss system was first used in a chess tournament in Zurich, Switzerland, in
      1895 — hence the name. The organizers needed a way to handle more players than a
      round robin would allow (which requires everyone to play everyone) without resorting
      to elimination. The solution: pair players with equal scores in each round, and
      after enough rounds, the rankings sort themselves out naturally. Over 130 years
      later, the format remains largely unchanged.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      How Swiss pairing works
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Before the first round, players are paired randomly (or by seeding). After each
      round, the pairing algorithm groups players by their current score and pairs
      players within the same score group against each other. The key rules are:
    </Text>
    {[
      'Players with the same number of wins are paired together whenever possible',
      'No two players can be paired against each other more than once',
      'When a score group has an odd number of players, one player receives a "bye" (a free win)',
      'Color assignments in chess (or similar constraints in other games) are balanced over multiple rounds',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20, marginTop: 12 }}>
      The result is that top players inevitably face other top players as the tournament
      progresses, producing meaningful matchups even without elimination. By the final
      round, the undefeated players meet each other to determine the champion.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      How many rounds does a Swiss tournament need?
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      The standard recommendation is Log₂(N) rounds, where N is the number of players —
      the same depth as a single elimination bracket. So 8 players need 3 rounds, 16
      players need 4 rounds, 32 players need 5 rounds, and 64 players need 6 rounds.
      This is dramatically fewer rounds than a round robin would require with the same
      number of players, making Swiss ideal for large events that cannot accommodate
      everyone playing everyone.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Tie-breaking in Swiss tournaments
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      When multiple players finish a Swiss tournament with the same win count, tie-breaking
      criteria determine final placements. The most common systems are:
    </Text>
    {[
      'Buchholz score — the sum of the scores of all your opponents (players who faced stronger opponents rank higher)',
      'Sonneborn-Berger — sum of the scores of opponents you beat, plus half the scores of opponents you drew against',
      'Direct encounter — if only two players are tied, who won their head-to-head match?',
      'Number of wins — more decisive wins rank higher than draws with the same point total',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Swiss vs Round Robin vs Elimination
    </Text>
    {[
      { label: 'vs Round Robin', desc: 'Swiss handles far more players in far fewer rounds. Round robin requires everyone to play everyone; Swiss produces similar quality matchups with Log₂(N) rounds instead of N−1 rounds.' },
      { label: 'vs Single Elimination', desc: 'Swiss never eliminates anyone, so weaker players still get to play and learn throughout the event. Single elimination is faster but offers no second chance.' },
      { label: 'vs Double Elimination', desc: 'Both give players a second chance, but Swiss scales to much larger fields. Double elimination becomes unwieldy above 64 players; Swiss works fine with hundreds.' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 4 }}>{item.label}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Best use cases for the Swiss system
    </Text>
    {[
      'Chess tournaments and clubs — the format was designed for chess and remains optimal for it',
      'Trading card game events (Magic: The Gathering, Pokémon TCG, Yu-Gi-Oh) — all major TCG tournaments use Swiss',
      'Large open tournaments with 32+ players where round robin is impractical',
      'Board game competitions where draws are common and tie-breaking needs to be systematic',
      'Any event where no one should be eliminated early and everyone should face meaningful competition',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Swiss tournaments in Tournaly
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Tournaly handles Swiss pairing automatically. Set the number of rounds, add your
      players, and generate the bracket. After each round, enter the results and Tournaly
      calculates the next round pairings based on current standings — no manual work
      required. Live standings update in real time as scores come in.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.textSecondary }}>
      The Swiss format is available on Tournaly Pro, alongside Double Elimination and
      King of the Hill. The free plan includes Single Elimination and Round Robin.
    </Text>
  </View>
);

const SwissContentEs: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      El sistema suizo es un formato diseñado para gestionar grandes grupos de jugadores
      de manera eficiente y al mismo tiempo producir enfrentamientos justos y significativos.
      Los jugadores nunca son eliminados — en cambio, cada ronda empareja a jugadores con
      registros similares. Después de un número determinado de rondas, la clasificación
      determina el ranking final. Es el formato estándar para los torneos de ajedrez en
      todo el mundo y cada vez más popular en juegos de cartas coleccionables, esports
      y competiciones de juegos de mesa.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      El origen del sistema suizo
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      El sistema suizo se usó por primera vez en un torneo de ajedrez en Zúrich, Suiza,
      en 1895 — de ahí el nombre. Los organizadores necesitaban una forma de gestionar
      más jugadores de los que un round robin permitiría (que requiere que todos jueguen
      contra todos) sin recurrir a la eliminación. La solución: emparejar jugadores con
      puntuaciones iguales en cada ronda y, después de suficientes rondas, los rankings
      se ordenan naturalmente. Más de 130 años después, el formato permanece prácticamente
      sin cambios.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Cómo funciona el emparejamiento suizo
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Antes de la primera ronda, los jugadores se emparejan aleatoriamente (o por
      clasificación). Después de cada ronda, el algoritmo de emparejamiento agrupa a los
      jugadores por su puntuación actual y empareja a jugadores dentro del mismo grupo
      de puntuación. Las reglas clave son:
    </Text>
    {[
      'Los jugadores con el mismo número de victorias se emparejan entre sí siempre que sea posible',
      'Dos jugadores nunca pueden enfrentarse más de una vez',
      'Cuando un grupo de puntuación tiene un número impar de jugadores, uno recibe un "bye" (victoria libre)',
      'Las asignaciones de color en ajedrez (u otras restricciones similares) se equilibran a lo largo de varias rondas',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20, marginTop: 12 }}>
      El resultado es que los jugadores de nivel alto inevitablemente se enfrentan entre
      sí a medida que avanza el torneo, produciendo enfrentamientos significativos sin
      necesidad de eliminación. En la ronda final, los jugadores invictos se enfrentan
      para determinar al campeón.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      ¿Cuántas rondas necesita un torneo suizo?
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      La recomendación estándar es Log₂(N) rondas, donde N es el número de jugadores —
      la misma profundidad que un bracket de eliminación simple. Así, 8 jugadores necesitan
      3 rondas, 16 jugadores necesitan 4 rondas, 32 jugadores necesitan 5 rondas y 64
      jugadores necesitan 6 rondas. Esto es dramáticamente menos rondas que las que
      requeriría un round robin con el mismo número de jugadores, lo que hace al suizo
      ideal para eventos grandes que no pueden permitirse que todos jueguen contra todos.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Mejores casos de uso del sistema suizo
    </Text>
    {[
      'Torneos y clubes de ajedrez — el formato fue diseñado para el ajedrez y sigue siendo óptimo para él',
      'Eventos de juegos de cartas coleccionables (Magic: The Gathering, Pokémon TCG, Yu-Gi-Oh) — todos los torneos grandes de JCC usan suizo',
      'Torneos abiertos grandes con 32+ jugadores donde el round robin es impracticable',
      'Competiciones de juegos de mesa donde los empates son comunes y el desempate necesita ser sistemático',
      'Cualquier evento donde nadie deba ser eliminado pronto y todos deban enfrentarse a competencia significativa',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Torneos suizos en Tournaly
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Tournaly gestiona el emparejamiento suizo automáticamente. Establece el número de
      rondas, añade tus jugadores y genera el bracket. Después de cada ronda, introduce
      los resultados y Tournaly calcula los emparejamientos de la siguiente ronda basándose
      en la clasificación actual — sin trabajo manual. La clasificación en directo se
      actualiza en tiempo real conforme llegan las puntuaciones.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.textSecondary }}>
      El formato suizo está disponible en Tournaly Pro, junto con la Doble Eliminación y
      King of the Hill. El plan gratuito incluye Eliminación Simple y Round Robin.
    </Text>
  </View>
);

export const post: BlogPost = {
  slug: '2026-03-06-swiss-system-tournament-guide',
  title: "Swiss System Tournaments: The Complete Organizer's Guide",
  titleEs: 'Torneos con Sistema Suizo: La Guía Completa para Organizadores',
  date: '2026-03-06',
  summary: 'The Swiss system tournament explained — how pairing works, how many rounds you need, tie-breaking methods, and why chess, TCG, and esports events use Swiss over round robin or elimination.',
  summaryEs: 'El sistema suizo de torneos explicado — cómo funciona el emparejamiento, cuántas rondas necesitas, métodos de desempate y por qué el ajedrez, los JCC y los esports usan el suizo frente al round robin o la eliminación.',
  tags: ['Guide', 'Formats', 'Swiss System'],
  Content: SwissContent,
  ContentEs: SwissContentEs,
};
